import { useMutation } from '@apollo/client/react';
import { type FC, useState } from 'react';

import LabelledValue from '@zen/Components/LabelledValue';
import PackagingTypesSelect from '@zen/Components/PackagingTypeSelect/PackagingTypeSelect';
import ValueWithDefault from '@zen/Components/ValueWithDefault';
import { useNotification } from '@zen/utils/hooks/useNotification';
import type { IOkOrErrorResult } from '@zen/utils/OkOrErrorResult';
import { performMutation } from '@zen/utils/performMutation';

import {
  UpdatePackagingTypesDocument,
  type UpdatePackagingTypesMutationResult,
  type UpdatePackagingTypesMutationVariables
} from './graphql';

interface Props {
  canUpdatePackagingTypes: boolean;
  cargoId: string;
  packagingTypes: string;
}

const PackagingTypesInlineField: FC<Props> = ({ canUpdatePackagingTypes, cargoId, packagingTypes }) => {
  const [packagingTypesValue, setPackagingTypesValue] = useState<string>(packagingTypes);

  const { addSuccess, addError } = useNotification();

  const [updatePackagingTypes] = useMutation<UpdatePackagingTypesMutationResult, UpdatePackagingTypesMutationVariables>(
    UpdatePackagingTypesDocument
  );

  const label: string = 'Packaging types';

  const handleChange = (value: string): Promise<IOkOrErrorResult> => {
    return performMutation({
      mutationFn: () =>
        updatePackagingTypes({
          variables: {
            input: {
              cargoId,
              packagingTypes: value || ''
            }
          }
        }),
      onError: () => addError(),
      onSuccess: () => {
        addSuccess(`${label} updated.`);
        setPackagingTypesValue(value);
      }
    });
  };

  if (!canUpdatePackagingTypes) {
    return (
      <LabelledValue label={label} variant="default">
        <ValueWithDefault value={packagingTypesValue} />
      </LabelledValue>
    );
  }

  return (
    <div className="-ml-3">
      <PackagingTypesSelect
        onChange={handleChange}
        label={label}
        name="packaging-types"
        placeholder="Select packaging types"
        value={packagingTypesValue}
      />
    </div>
  );
};

export default PackagingTypesInlineField;
